import React from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FiVolume2, FiVolumeX } from 'react-icons/fi';
import styles from './HoverPreviewPopup.module.css';

const POPUP_SCALE = 1.5;
const VIEWPORT_MARGIN = 16;

/**
 * Floating "mini modal" that appears above a hovered tile — video preview on top, title and
 * metadata below. Rendered into document.body via a portal so it's never clipped by a row's
 * horizontal scroll container, and positioned with fixed coordinates derived from the anchor
 * tile's viewport rect.
 */
export default function HoverPreviewPopup({
  open, rect, videoRef, trailerUrl, thumbnailUrl,
  previewReady, onPreviewPlaying,
  muted, onToggleMute,
  title, meta,
  onMouseEnter, onMouseLeave,
}) {
  if (!rect) return null;

  const width = rect.width * POPUP_SCALE;
  let left = rect.left + rect.width / 2 - width / 2;
  left = Math.max(VIEWPORT_MARGIN, Math.min(left, window.innerWidth - width - VIEWPORT_MARGIN));
  // Adjust top so it scales up from the center, maybe pushing it slightly higher
  const top = rect.top - (rect.height * (POPUP_SCALE - 1)) / 2 - 12;

  return createPortal(
    <AnimatePresence>
      {open && (
        <motion.div
          className={styles.popup}
          style={{ top, left, width }}
          initial={{ opacity: 0, scale: 0.85, y: 15 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.85, y: 15 }}
          transition={{ duration: 0.3, ease: [0.25, 1, 0.5, 1] }}
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}
        >
          <div className={styles.videoWrap}>
            <img
              src={thumbnailUrl}
              alt=""
              className={styles.poster}
              style={{ opacity: previewReady ? 0 : 1 }}
            />
            {trailerUrl && (
              <video
                ref={videoRef}
                className={styles.video}
                style={{ opacity: previewReady ? 1 : 0 }}
                muted={muted}
                loop
                playsInline
                onPlaying={onPreviewPlaying}
              />
            )}
            {previewReady && (
              <button
                type="button"
                className={styles.muteBtn}
                onClick={onToggleMute}
                aria-label={muted ? 'Unmute preview' : 'Mute preview'}
              >
                {muted ? <FiVolumeX /> : <FiVolume2 />}
              </button>
            )}
            <div className={styles.videoGradient} />
          </div>

          <div className={styles.body}>
            <h4 className={styles.title}>{title}</h4>
            {meta && <div className={styles.meta}>{meta}</div>}
          </div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
}
